import { FileCheck2, FileText, Globe2, ListChecks, Lock } from "lucide-react";
import { Sheet } from "../../components/Sheet";
import { Button } from "../../components/primitives";

/**
 * First-run walkthrough. Dismissal is persisted under `adc-walkthrough-seen-v1`.
 *
 * IT IS A REAL MODAL, and it composes the Sheet primitive for that: focus moved into the
 * dialog, Tab cycled inside, Escape and backdrop click and a visible 44px close button all
 * dismiss, focus restored to the trigger, body scroll locked. None of that is re-improvised
 * here, and none of it changed in this pass.
 *
 * WHAT CHANGED: this is a DOCUMENT product whose onboarding never once showed a document.
 * Four grey paragraphs described the contract; now the hero SHOWS it. The miniature note is
 * rendered in Source Serif 4 — the app speaks sans, the document speaks serif, and that one
 * typographic split is the whole point of the product — and it carries the two things a
 * first-time operator most needs to trust: a proposed insertion, tinted with the same
 * --diff-add token the review screen uses, and a protected figure sealed in amber.
 *
 * HONESTY. The hero is an ILLUSTRATION OF THE MECHANISM and nothing more. Its insertion is
 * the literal placeholder "[partner organization]" — never a real funder name — it is
 * captioned "Example — no run has started", and it is aria-hidden, so assistive tech reads
 * the four steps, which carry the meaning. It fabricates no run, no partner, no progress
 * number and no QA result, and it opens no path to export.
 */
export function WalkthroughOverlay({ onClose }: { onClose: () => void }) {
  const steps = [
    {
      icon: <FileText aria-hidden="true" />,
      title: "Choose the source",
      body: "Use the packaged ATLAS note, or update the authoritative source in Settings."
    },
    {
      icon: <Globe2 aria-hidden="true" />,
      title: "Confirm the partner",
      body: "Enter the organization, review the website suggestions, and select the official URL."
    },
    {
      icon: <ListChecks aria-hidden="true" />,
      title: "Review proposed edits",
      body: "Every recommended change is shown as before, after and why, before the document is created."
    },
    {
      icon: <FileCheck2 aria-hidden="true" />,
      title: "Approve and package",
      body: "Final QA runs only once every edit is decided. Approved packages stay in Library."
    }
  ];

  return (
    <Sheet
      eyebrow="First visit"
      title="How a document moves through ATLAS"
      labelledBy="walkthrough-title"
      testId="walkthrough"
      onClose={onClose}
      footer={
        <Button variant="primary" block data-testid="walkthrough-dismiss" onClick={onClose}>
          Start a run
        </Button>
      }
    >
      {/* DOM ORDER IS THE PHONE'S READING ORDER: the contract sentence, then the document that
          demonstrates it, then the path. On desktop the grid re-seats the document into a left
          column spanning both rows — no reordering, just placement. */}
      <div className="walkthrough-body">
        <p className="walkthrough-lead">
          One canonical note, personalized for one partner. You decide on every proposed edit
          before the document is created.
        </p>

        <div className="walkthrough-hero" aria-hidden="true">
          <article className="walkthrough-doc">
            <p className="walkthrough-doc-label">ATLAS DPI · Concept note</p>
            <h4 className="walkthrough-doc-title">
              Digital Public Infrastructure for Equitable Health Systems
            </h4>
            <hr className="walkthrough-doc-rule" />
            <div className="walkthrough-doc-body">
              <p>
                The programme builds shared digital rails for health systems, and the case is put
                in the language of <ins>[partner organization]</ins> and its stated priorities.
              </p>
              {/* No punctuation may follow the chip: its trailing tint padding would leave a
                  visible gap before the comma. The sentence is built so a word comes next. */}
              <p>
                The note runs to{" "}
                <span className="walkthrough-doc-locked">
                  <Lock aria-hidden="true" />
                  39 pages
                </span>{" "}
                and every sealed figure in it stays exactly as written.
              </p>
            </div>
          </article>
          <p className="walkthrough-doc-caption">Example — no run has started.</p>
        </div>

        <ol className="walkthrough-steps">
          {steps.map((step) => (
            <li className="walkthrough-step" key={step.title}>
              <span className="walkthrough-step-icon" aria-hidden="true">
                {step.icon}
              </span>
              <div className="walkthrough-step-text">
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </Sheet>
  );
}
